import type {
  Arrangement,
  Cell,
  CubeColor,
  CubeHellPhase,
  CubeHellPlayerId,
  QueryRecord,
} from "@cloudnest/cube-hell-engine";
import { CUBE_COLORS, cellIndex, GRID } from "@cloudnest/cube-hell-engine";
import { useState } from "react";

interface CubeHellViewProps {
  readonly phase: CubeHellPhase;
  readonly currentPlayerId: CubeHellPlayerId;
  readonly queryCounts: Readonly<Record<CubeHellPlayerId, number>>;
  readonly log: readonly QueryRecord[];
  readonly names: Readonly<Record<CubeHellPlayerId, string>>;
  /** Per-player true arrangement, or null when masked (observer view). */
  readonly truth: Readonly<Record<CubeHellPlayerId, Arrangement | null>>;
  readonly winnerId: CubeHellPlayerId | null;
}

const PHASE_LABEL: Record<CubeHellPhase, string> = {
  query: "질문/추리",
  "game-over": "종료",
};

const COLOR_LABEL: Record<CubeColor, string> = {
  red: "빨강",
  yellow: "노랑",
  green: "초록",
  blue: "파랑",
};

const PLAYERS: readonly CubeHellPlayerId[] = ["p1", "p2"];
const LAYERS = [0, 1, 2] as const;
const ROWS = [0, 1, 2] as const;
const COLS = [0, 1, 2] as const;

/** CELL-query knowledge about a target, keyed by cellIndex. */
function knownCellsFor(
  log: readonly QueryRecord[],
  targetId: CubeHellPlayerId,
): Map<number, Cell> {
  const known = new Map<number, Cell>();
  for (const record of log) {
    if (record.kind === "cell" && record.targetId === targetId) {
      known.set(cellIndex(record.x, record.y, record.z), record.result);
    }
  }
  return known;
}

/** Known z-layer colour counts for a target (from LAYER queries). */
function layerCountsFor(
  log: readonly QueryRecord[],
  targetId: CubeHellPlayerId,
  z: number,
): string {
  // Latest count per colour for this z-layer — scan the public log backwards
  // (no allocation; the most recent matching LAYER answer wins).
  const counts = new Map<CubeColor, number>();
  for (
    let i = log.length - 1;
    i >= 0 && counts.size < CUBE_COLORS.length;
    i -= 1
  ) {
    const entry = log[i];
    if (
      entry.kind === "layer" &&
      entry.targetId === targetId &&
      entry.axis === "z" &&
      entry.index === z &&
      !counts.has(entry.color)
    ) {
      counts.set(entry.color, entry.count);
    }
  }
  return CUBE_COLORS.filter((color) => counts.has(color))
    .map((color) => `${COLOR_LABEL[color]} ${counts.get(color)}`)
    .join(" · ");
}

function CellTile({
  truth,
  known,
}: {
  truth: Cell | null;
  known: Cell | undefined;
}) {
  // Recap reveals the true cell (data-truth-color). Otherwise show only what was
  // publicly queried (data-known-color), else a masked "?".
  if (truth !== null) {
    const isEmpty = truth === "empty";
    return (
      <div
        data-truth-color={truth}
        className="flex aspect-square items-center justify-center rounded-sm font-bold text-xs"
        style={{
          background: isEmpty
            ? "var(--cn-cube-hell-cell-empty)"
            : `var(--cn-cube-hell-${truth})`,
          color: isEmpty ? "var(--cn-cube-hell-text-muted)" : "#0c0f15",
        }}
      >
        {isEmpty ? "·" : COLOR_LABEL[truth as CubeColor][0]}
      </div>
    );
  }
  if (known !== undefined) {
    const isEmpty = known === "empty";
    return (
      <div
        data-known-color={known}
        className="flex aspect-square items-center justify-center rounded-sm font-bold text-xs"
        style={{
          background: isEmpty
            ? "var(--cn-cube-hell-cell-empty)"
            : `var(--cn-cube-hell-${known})`,
          color: isEmpty ? "var(--cn-cube-hell-text-muted)" : "#0c0f15",
        }}
      >
        {isEmpty ? "·" : COLOR_LABEL[known as CubeColor][0]}
      </div>
    );
  }
  return (
    <div
      data-cell="unknown"
      className="flex aspect-square items-center justify-center rounded-sm font-bold text-xs"
      style={{
        background: "var(--cn-cube-hell-cell-unknown)",
        color: "var(--cn-cube-hell-text-muted)",
      }}
    >
      ?
    </div>
  );
}

function DeductionBoard({
  targetId,
  name,
  layer,
  log,
  truth,
}: {
  targetId: CubeHellPlayerId;
  name: string;
  layer: number;
  log: readonly QueryRecord[];
  truth: Arrangement | null;
}) {
  const known = knownCellsFor(log, targetId);
  const annotation = layerCountsFor(log, targetId, layer);
  return (
    <div
      data-testid={`cube-hell-deduction-${targetId}`}
      className="flex flex-col gap-1.5 rounded-md border p-2"
      style={{
        borderColor: "var(--cn-cube-hell-border)",
        background: "var(--cn-cube-hell-panel)",
      }}
    >
      <div
        className="text-center font-semibold text-xs"
        style={{ color: `var(--cn-cube-hell-${targetId})` }}
      >
        {name}의 배열
      </div>
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${GRID}, minmax(0, 1fr))` }}
      >
        {ROWS.map((y) =>
          COLS.map((x) => {
            const index = cellIndex(x, y, layer);
            return (
              <CellTile
                key={index}
                truth={truth ? truth[index] : null}
                known={known.get(index)}
              />
            );
          }),
        )}
      </div>
      <div
        className="min-h-[14px] text-center text-[10px]"
        style={{ color: "var(--cn-cube-hell-text-muted)" }}
      >
        {annotation || "층 정보 미확인"}
      </div>
    </div>
  );
}

function logSummary(
  record: QueryRecord,
  names: Readonly<Record<CubeHellPlayerId, string>>,
): string {
  const who = names[record.askerId];
  switch (record.kind) {
    case "cell": {
      const result =
        record.result === "empty" ? "빈칸" : COLOR_LABEL[record.result];
      return `${who} · 칸(${record.x},${record.y},${record.z}) → ${result}`;
    }
    case "layer":
      return `${who} · ${record.index + 1}층 ${COLOR_LABEL[record.color]} → ${record.count}개`;
    case "guess":
      return `${who} · 추리 선언 → ${record.correct ? "정답" : `오답(${record.matched}/27)`}`;
  }
}

/**
 * Read-only 큐브지옥 board. Renders both players' deduction boards as z-layer
 * slices: publicly-queried cells fill in (data-known-color), unqueried cells stay
 * masked ("?"), and the recap reveals the true arrangements (data-truth-color).
 * Pure presentation, driven by props from the replay screen.
 */
export function CubeHellView({
  phase,
  currentPlayerId,
  queryCounts,
  log,
  names,
  truth,
  winnerId,
}: CubeHellViewProps) {
  const [layer, setLayer] = useState(0);
  const recentLog = log.slice(-6);

  return (
    <div
      data-testid="cube-hell-board"
      className="flex w-full flex-col gap-3 rounded-md p-3"
      style={{
        background: "var(--cn-cube-hell-panel-alt)",
        maxWidth: "min(94vw, 560px)",
      }}
    >
      <div className="flex items-center justify-between text-sm">
        <span
          data-testid="cube-hell-phase"
          className="rounded-full px-2 py-0.5 text-xs"
          style={{
            background: "var(--cn-cube-hell-panel)",
            color: "var(--cn-cube-hell-text)",
          }}
        >
          {PHASE_LABEL[phase]}
        </span>
        <span
          className="text-xs"
          style={{ color: "var(--cn-cube-hell-text-muted)" }}
        >
          {phase === "game-over" && winnerId
            ? `${names[winnerId]} 승`
            : `차례: ${names[currentPlayerId]}`}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 text-xs">
        {PLAYERS.map((playerId) => (
          <span
            key={playerId}
            data-testid={`cube-hell-queries-${playerId}`}
            className="font-semibold"
            style={{ color: `var(--cn-cube-hell-${playerId})` }}
          >
            {names[playerId]} · 질문 {queryCounts[playerId]}
          </span>
        ))}
      </div>

      <div
        data-testid="cube-hell-layer-tabs"
        className="flex items-center justify-center gap-1.5"
      >
        {LAYERS.map((z) => (
          <button
            key={z}
            type="button"
            data-testid={`cube-hell-layer-tab-${z}`}
            data-active={layer === z ? "true" : "false"}
            onClick={() => setLayer(z)}
            className="rounded-md border px-3 py-1 font-semibold text-xs"
            style={{
              borderColor:
                layer === z
                  ? "var(--cn-cube-hell-accent)"
                  : "var(--cn-cube-hell-border)",
              background:
                layer === z
                  ? "var(--cn-cube-hell-accent)"
                  : "var(--cn-cube-hell-panel)",
              color: layer === z ? "#0c0f15" : "var(--cn-cube-hell-text)",
            }}
          >
            {z + 1}층
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        {PLAYERS.map((targetId) => (
          <DeductionBoard
            key={targetId}
            targetId={targetId}
            name={names[targetId]}
            layer={layer}
            log={log}
            truth={truth[targetId]}
          />
        ))}
      </div>

      <div
        data-testid="cube-hell-query-log"
        className="flex flex-col gap-0.5 rounded-md border px-2 py-1.5 text-[11px]"
        style={{
          borderColor: "var(--cn-cube-hell-border)",
          background: "var(--cn-cube-hell-panel)",
          color: "var(--cn-cube-hell-text-muted)",
        }}
      >
        {recentLog.length === 0 ? (
          <span className="text-center">아직 질문이 없습니다</span>
        ) : (
          recentLog.map((record) => (
            <span key={record.seq}>
              #{record.seq} {logSummary(record, names)}
            </span>
          ))
        )}
      </div>
    </div>
  );
}
